import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { constants, currencyString } from "@qgiv/core-js";
import { createEntityReplaceForRegistration } from "@qgiv/p2p-registration";
import OptionalDonation from "./OptionalDonation";
import { selectHasParentRegistration } from "../../../redux/slices/appSettingsSlice";
import { selectParentRegistration } from "../../../redux/slices/parentRegistrationSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import {
    selectAllRegistration,
    selectRegistrationTotals,
    updateRegistrationTotals,
} from "../../../redux/slices/registrationSlice";
import { selectTeams } from "../../../redux/slices/teamsSlice";
import { selectAllClassifications } from "../../../redux/slices/classificationsSlice";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";

const ConnectedOptionalDonation = ({ controlId }) => {
    const {
        ENUMS: { DefaultView },
    } = constants;

    const hasParentRegistration = useSelector(selectHasParentRegistration);
    const parentRegistration = useSelector(selectParentRegistration);
    const { maxAmt, currency, title } = useSelector(selectAllFormSettings);
    const {
        enableOptionalDonation,
        enableDonationToParticipant,
        enableDonationToTeam,
        enableDonationToEvent,
        enableClassificationForDonation,
    } = useSelector(selectAllP2pSettings);
    const displayOptionalDonation = enableOptionalDonation;
    const classifications = useSelector(selectAllClassifications);
    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.REGISTRATION_CHECKOUT_STEP,
            state,
        ),
    );

    const {
        registrations = [],
        classification = {},
        giftAssist,
    } = useSelector(selectAllRegistration);
    const {
        giftAssistAmount,
        optionalDonationAmount = 0.0,
        total = 0.0,
    } = useSelector(selectRegistrationTotals);
    const teams = useSelector(selectTeams);

    const { options } = control;

    const {
        sectionHeading = "Add an Optional Gift",
        sectionHeadingForNonFundraisingParticipants = "Add an Optional Gift",
        description = "%FirstName%, kick-start your fundraising by making a donation!",
        descriptionForNonFundraisingParticipants = "%FirstName%, will you consider donating?",
        maxDonationAmountLabel = "%MaxDonationAmount% max online gift",
        recipientFinePrintLabel = "It will go toward your fundraising goal.",
        recipientFinePrintLabelForNonFundraisingParticipants = "It will go toward %Entity%’s fundraising goal.",
    } = options;

    const registrationFirstName =
        registrations.length > 0 && registrations[0].First_Name
            ? registrations[0].First_Name
            : "";

    const First_Name = hasParentRegistration
        ? parentRegistration.First_Name
        : registrationFirstName;

    const displayNameForHasParentRegistration =
        registrations.length > 0 && registrations[0].First_Name
            ? registrations[0].First_Name
            : "";

    const isNfp = !!(
        registrations.length > 0 &&
        registrations[0].Is_Nfp &&
        registrations[0].Is_Nfp === true
    );

    const entityReplace = createEntityReplaceForRegistration(
        registrations,
        teams,
        classification,
        classifications,
        enableDonationToTeam,
        enableDonationToEvent,
        enableClassificationForDonation,
        title,
    );

    const entityReplacedRecipientFinePrintLabelForNFPs =
        recipientFinePrintLabelForNonFundraisingParticipants.replace(
            "%Entity%",
            entityReplace || "",
        );

    const optionalDonationProps = {
        displayOptionalDonation,
        isNfp,
        sectionHeading,
        sectionHeadingForNonFundraisingParticipants,
        description: description.replace(
            "%FirstName%",
            First_Name || "Participant",
        ),
        descriptionForNonFundraisingParticipants:
            descriptionForNonFundraisingParticipants.replace(
                "%FirstName%",
                First_Name || "Participant",
            ),
        // eslint-disable-next-line no-nested-ternary
        recipientFinePrintLabel: enableDonationToParticipant
            ? hasParentRegistration
                ? // This is not customizable in CMS, it is only for fundraising center registrations
                  // where the optional donation will actually go to the sub's account
                  `It will go toward ${displayNameForHasParentRegistration}'s fundraising goal.`
                : recipientFinePrintLabel
            : entityReplacedRecipientFinePrintLabelForNFPs,
        // donations made for a NFP additional registration through the
        // fundraising center will be attributed to the primary registration
        recipientFinePrintLabelForNonFundraisingParticipants:
            hasParentRegistration && enableDonationToParticipant
                ? recipientFinePrintLabel
                : entityReplacedRecipientFinePrintLabelForNFPs,
        maxDonationAmountLabel:
            maxAmt && Number(maxAmt) > 0
                ? maxDonationAmountLabel.replace(
                      "%MaxDonationAmount%",
                      currencyString(maxAmt, currency),
                  )
                : "",
        optionalDonationAmount,
        total,
        giftAssist,
        giftAssistAmount,
        updateRegistrationTotals,
    };

    return <OptionalDonation {...optionalDonationProps} />;
};

ConnectedOptionalDonation.propTypes = {
    controlId: PropTypes.number.isRequired,
};

export default ConnectedOptionalDonation;
